import React, { useState, useCallback, useEffect, useRef } from 'react';
import { t } from '@/locales/strings';
import { useModalA11y } from '@/hooks/useModalA11y';

/**
 * TermPopover — a tappable inline word with a plain-language
 * explanation behind it. Wraps a clinical term ("squeeze",
 * "release", "central layer") with a subtle dotted underline + a
 * superscript question-mark glyph so the term reads as a learnable
 * thing rather than jargon. Tapping opens a small centered modal
 * with the explanation and a single dismiss button.
 *
 * Catalog of explanations lives in src/locales/strings.js under
 * `term_<key>_title` / `term_<key>_body`. Adding a new term is a
 * two-key copy edit; no plumbing.
 *
 * Usage:
 *   <TermPopover termKey="squeeze" lang={lang}>
 *     squeeze
 *   </TermPopover>
 *
 * Accessibility:
 *   - The wrapping span is a real button so VoiceOver / TalkBack
 *     announce it as actionable.
 *   - The modal traps focus + closes on Escape via useModalA11y.
 *   - aria-haspopup + aria-expanded reflect the open state.
 */
export default function TermPopover({ termKey, lang = 'en', children }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const close = useCallback(() => {
    setOpen(false);
    // Return focus to the inline term when the modal closes so
    // keyboard navigation isn't dropped on the body.
    queueMicrotask(() => triggerRef.current?.focus());
  }, []);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('term_aria_open', lang).replace('{w}', String(children))}
        style={{
          all: 'unset',
          cursor: 'pointer',
          color: 'inherit',
          font: 'inherit',
          borderBottom: '1px dotted currentColor',
          opacity: 0.95,
        }}
      >
        {children}
        <span aria-hidden="true" style={{
          fontSize: '0.7em',
          verticalAlign: 'super',
          marginLeft: 2,
          opacity: 0.6,
        }}>{'?'}</span>
      </button>
      {open && <TermDialog termKey={termKey} lang={lang} onClose={close} />}
    </>
  );
}

function TermDialog({ termKey, lang, onClose }) {
  const dialogRef = useModalA11y({ open: true, onClose });
  // Hard-cap the modal escape: a tappable overlay closes on
  // backdrop click without trapping the user in the dialog.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bb-term-title"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(28, 14, 22, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        animation: 'bb-term-fade 220ms ease-out',
      }}
    >
      <div style={{
        width: '100%',
        maxWidth: 360,
        background: '#fff',
        borderRadius: 22,
        padding: '22px 22px 18px',
        boxShadow: '0 28px 60px -20px rgba(45,31,38,.45)',
        animation: 'bb-term-pop 280ms cubic-bezier(0.32,0.72,0,1)',
      }}>
        <div style={{
          font: '600 9px/1 var(--font-ui, Manrope)',
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: 'var(--primary-dark, #a85c4b)',
          marginBottom: 6,
        }}>
          {t('term_eyebrow', lang)}
        </div>
        <h2
          id="bb-term-title"
          style={{
            margin: '0 0 8px',
            fontFamily: 'var(--font-title, Fraunces), serif',
            fontSize: 22,
            lineHeight: 1.2,
            color: 'var(--ink, #2d1f26)',
          }}
        >
          {t(`term_${termKey}_title`, lang)}
        </h2>
        <p style={{
          margin: '0 0 18px',
          fontFamily: 'var(--font-body, Manrope)',
          fontSize: 15,
          lineHeight: 1.5,
          color: 'var(--ink-dim, #6b5560)',
          textWrap: 'balance',
        }}>
          {t(`term_${termKey}_body`, lang)}
        </p>
        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            minHeight: 44,
            borderRadius: 999,
            border: 'none',
            background:
              'linear-gradient(180deg, var(--primary-light, #e3a193) 0%, var(--primary, #c97a68) 100%)',
            color: '#fff',
            font: '600 13px/1 var(--font-ui, Manrope)',
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {t('term_close', lang)}
        </button>
      </div>
      <style>{`
        @keyframes bb-term-fade {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes bb-term-pop {
          0%   { opacity: 0; transform: translateY(8px) scale(0.98); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
